import React, { useState, useRef, useEffect, DragEvent } from 'react';
import {
  Box,
  Button,
  Slider,
  Typography,
  useTheme,
  IconButton,
  Stack,
  List,
  ListItem,
  LinearProgress,
  ToggleButtonGroup,
  ToggleButton,
  CircularProgress,
  Paper,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import SaveIcon from '@mui/icons-material/Save';
import CropIcon from '@mui/icons-material/Crop';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import { useTranslation } from 'react-i18next';
import { uploadToScreenApp } from './common';

interface SplitPart {
  blob: Blob;
  url: string;
  name: string;
  duration: number;
  uploading: boolean;
  progress: number;
}

const AudioCutter: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [audioFile, setAudioFile] = useState<File | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [cutPoint, setCutPoint] = useState<number>(0);
  const [trimRange, setTrimRange] = useState<[number, number]>([0, 100]);
  const [mode, setMode] = useState<'trim' | 'split'>('trim');
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [splitParts, setSplitParts] = useState<SplitPart[]>([]);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const previewRefs = useRef<{ [key: string]: HTMLAudioElement | null }>({});
  const [error, setError] = useState<string | null>(null);
  const [previewStates, setPreviewStates] = useState<{
    [key: string]: boolean;
  }>({});

  const handleFileUpload = (file: File) => {
    if (!file.type.startsWith('audio/')) {
      setError(t('audioCutter.messages.invalidFile'));
      return;
    }

    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
    }

    setAudioFile(file);
    const url = URL.createObjectURL(file);
    setAudioUrl(url);
    setCutPoint(0);
    setCurrentTime(0);
    setDuration(0);
    setIsPlaying(false);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const handleDragEnter = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const handlePlayPause = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleSliderChange = (_: Event, newValue: number | number[]) => {
    const time = newValue as number;
    setCutPoint(time);
    if (audioRef.current) {
      audioRef.current.currentTime = time;
      setCurrentTime(time);
    }
  };

  const handleTrimRangeChange = (_: Event, newValue: number | number[]) => {
    const [start, end] = newValue as number[];
    setTrimRange([start, end]);
    if (audioRef.current) {
      audioRef.current.currentTime = (start / 100) * duration;
      setCurrentTime((start / 100) * duration);
    }
  };

  const handleModeChange = (
    _: React.MouseEvent<HTMLElement>,
    newMode: 'trim' | 'split'
  ) => {
    if (newMode !== null) {
      setMode(newMode);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const encodePCMToWav = (buffer: AudioBuffer): Blob => {
    const interleaved = new Float32Array(
      buffer.length * buffer.numberOfChannels
    );
    let position = 0;
    for (let i = 0; i < buffer.length; i += 1) {
      for (let channel = 0; channel < buffer.numberOfChannels; channel += 1) {
        interleaved[position] = buffer.getChannelData(channel)[i];
        position += 1;
      }
    }

    const wavBuffer = new ArrayBuffer(44 + interleaved.length * 2);
    const dataView = new DataView(wavBuffer);

    // Write WAV header
    const writeString = (view: DataView, pos: number, string: string) => {
      for (let i = 0; i < string.length; i += 1) {
        view.setUint8(pos + i, string.charCodeAt(i));
      }
    };

    writeString(dataView, 0, 'RIFF');
    dataView.setUint32(4, 36 + interleaved.length * 2, true);
    writeString(dataView, 8, 'WAVE');
    writeString(dataView, 12, 'fmt ');
    dataView.setUint32(16, 16, true);
    dataView.setUint16(20, 1, true);
    dataView.setUint16(22, buffer.numberOfChannels, true);
    dataView.setUint32(24, buffer.sampleRate, true);
    dataView.setUint32(28, buffer.sampleRate * 4, true);
    dataView.setUint16(32, buffer.numberOfChannels * 2, true);
    dataView.setUint16(34, 16, true);
    writeString(dataView, 36, 'data');
    dataView.setUint32(40, interleaved.length * 2, true);

    // Write PCM data
    for (let i = 0; i < interleaved.length; i += 1) {
      const sample = Math.max(-1, Math.min(1, interleaved[i]));
      dataView.setInt16(
        44 + i * 2,
        sample < 0 ? sample * 0x8000 : sample * 0x7fff,
        true
      );
    }

    return new Blob([wavBuffer], { type: 'audio/wav' });
  };

  const handlePreviewPlayPause = (partUrl: string) => {
    const audio = previewRefs.current[partUrl];
    if (audio) {
      if (previewStates[partUrl]) {
        audio.pause();
      } else {
        // Pause all other previews
        Object.entries(previewRefs.current).forEach(([url, element]) => {
          if (url !== partUrl && element) {
            element.pause();
          }
        });
        setPreviewStates((prev) => {
          const newStates = { ...prev };
          Object.keys(newStates).forEach((key) => {
            newStates[key] = false;
          });
          return newStates;
        });
        audio.play();
      }
      setPreviewStates((prev) => ({ ...prev, [partUrl]: !prev[partUrl] }));
    }
  };

  const handleSave = async (part: SplitPart, index: number) => {
    try {
      // Update uploading state for this part
      setSplitParts((prev) =>
        prev.map((p, i) =>
          i === index ? { ...p, uploading: true, progress: 0 } : p
        )
      );

      await uploadToScreenApp(
        part.blob,
        part.name,
        () => {}, // onStart is handled above
        (progress) => {
          setSplitParts((prev) =>
            prev.map((p, i) => (i === index ? { ...p, progress } : p))
          );
        },
        () => {
          // Reset upload state
          setSplitParts((prev) =>
            prev.map((p, i) =>
              i === index ? { ...p, uploading: false, progress: 0 } : p
            )
          );
        },
        (uploadError) => {
          setError(uploadError);
          setSplitParts((prev) =>
            prev.map((p, i) =>
              i === index ? { ...p, uploading: false, progress: 0 } : p
            )
          );
        },
        true // Open in new tab
      );
    } catch (err) {
      console.error('Error saving file:', err);
      setError('Failed to save file. Please try again.');
      setSplitParts((prev) =>
        prev.map((p, i) =>
          i === index ? { ...p, uploading: false, progress: 0 } : p
        )
      );
    }
  };

  const encodeAudioData = async (buffer: AudioBuffer): Promise<Blob> => {
    const offlineContext = new OfflineAudioContext(
      buffer.numberOfChannels,
      buffer.length,
      buffer.sampleRate
    );
    const source = offlineContext.createBufferSource();
    source.buffer = buffer;
    source.connect(offlineContext.destination);
    source.start();
    const renderedBuffer = await offlineContext.startRendering();
    return encodePCMToWav(renderedBuffer);
  };

  const handleCut = async () => {
    if (!audioFile || !audioUrl) {
      setError('Please upload an audio file first');
      return;
    }

    try {
      setIsProcessing(true);
      setError(null);

      const audioContext = new AudioContext();
      const response = await fetch(audioUrl);
      const arrayBuffer = await response.arrayBuffer();
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

      if (mode === 'split') {
        // Existing split logic
        const part1Samples = Math.floor(
          audioBuffer.length * (cutPoint / duration)
        );
        const part1Buffer = audioContext.createBuffer(
          audioBuffer.numberOfChannels,
          part1Samples,
          audioBuffer.sampleRate
        );
        const part2Buffer = audioContext.createBuffer(
          audioBuffer.numberOfChannels,
          audioBuffer.length - part1Samples,
          audioBuffer.sampleRate
        );

        for (
          let channel = 0;
          channel < audioBuffer.numberOfChannels;
          channel += 1
        ) {
          const part1Data = part1Buffer.getChannelData(channel);
          const part2Data = part2Buffer.getChannelData(channel);
          const originalData = audioBuffer.getChannelData(channel);

          part1Data.set(originalData.slice(0, part1Samples));
          part2Data.set(originalData.slice(part1Samples));
        }

        const part1Blob = await encodeAudioData(part1Buffer);
        const part2Blob = await encodeAudioData(part2Buffer);

        const originalName = audioFile.name.replace(/\.[^/.]+$/, '');
        const part1Url = URL.createObjectURL(part1Blob);
        const part2Url = URL.createObjectURL(part2Blob);

        splitParts.forEach((part) => {
          URL.revokeObjectURL(part.url);
        });

        setSplitParts([
          {
            blob: part1Blob,
            url: part1Url,
            name: `${originalName}_part1.wav`,
            duration: cutPoint,
            uploading: false,
            progress: 0,
          },
          {
            blob: part2Blob,
            url: part2Url,
            name: `${originalName}_part2.wav`,
            duration: duration - cutPoint,
            uploading: false,
            progress: 0,
          },
        ]);
      } else {
        // Trim mode logic
        const startSample = Math.floor(
          audioBuffer.length * (trimRange[0] / 100)
        );
        const endSample = Math.floor(audioBuffer.length * (trimRange[1] / 100));
        const trimmedLength = endSample - startSample;

        const trimmedBuffer = audioContext.createBuffer(
          audioBuffer.numberOfChannels,
          trimmedLength,
          audioBuffer.sampleRate
        );

        for (
          let channel = 0;
          channel < audioBuffer.numberOfChannels;
          channel += 1
        ) {
          const trimmedData = trimmedBuffer.getChannelData(channel);
          const originalData = audioBuffer.getChannelData(channel);
          trimmedData.set(originalData.slice(startSample, endSample));
        }

        const trimmedBlob = await encodeAudioData(trimmedBuffer);
        const trimmedUrl = URL.createObjectURL(trimmedBlob);
        const originalName = audioFile.name.replace(/\.[^/.]+$/, '');

        splitParts.forEach((part) => {
          URL.revokeObjectURL(part.url);
        });

        setSplitParts([
          {
            blob: trimmedBlob,
            url: trimmedUrl,
            name: `${originalName}_trimmed.wav`,
            duration: ((trimRange[1] - trimRange[0]) / 100) * duration,
            uploading: false,
            progress: 0,
          },
        ]);
      }
    } catch (err) {
      console.error('Error processing audio:', err);
      setError('Failed to process audio. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleNewFile = () => {
    // Clean up existing resources
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
    }
    splitParts.forEach((part) => {
      URL.revokeObjectURL(part.url);
    });

    // Reset all state
    setAudioFile(null);
    setAudioUrl(null);
    setCutPoint(0);
    setTrimRange([0, 100]);
    setCurrentTime(0);
    setDuration(0);
    setIsPlaying(false);
    setSplitParts([]);
    setError(null);
  };

  const handleTryAgain = () => {
    // Just clear the output and reset editing state
    splitParts.forEach((part) => {
      URL.revokeObjectURL(part.url);
    });
    setSplitParts([]);
    setCutPoint(0);
    setTrimRange([0, 100]);
    setCurrentTime(0);
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
    }
    setError(null);
  };

  useEffect(() => {
    return () => {
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
      splitParts.forEach((part) => {
        URL.revokeObjectURL(part.url);
      });
    };
  }, [audioUrl, splitParts]);

  const getButtonText = () => {
    if (isProcessing) return t('audioCutter.messages.processing');
    return mode === 'split'
      ? t('audioCutter.controls.cut')
      : t('audioCutter.controls.trim');
  };

  return (
    <Box sx={{ p: 3 }}>
      {!audioFile ? (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Box sx={{ maxWidth: '60rem', width: '100%' }}>
            <Paper
              elevation={0}
              variant="outlined"
              component="label"
              sx={{
                p: 4,
                textAlign: 'center',
                cursor: 'pointer',
                borderStyle: 'dashed',
                borderWidth: 2,
                borderRadius: '12px',
                bgcolor: isDragging ? 'action.hover' : 'background.paper',
                borderColor: isDragging ? 'primary.main' : 'grey.300',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: 'action.hover',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                },
                minHeight: '300px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              <input
                type="file"
                accept="audio/*"
                hidden
                onChange={handleInputChange}
              />
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                <CloudUploadIcon
                  sx={{
                    fontSize: 64,
                    color: isDragging ? 'primary.main' : 'grey.400',
                    mb: 2,
                  }}
                />

                <Typography variant="h4" sx={{ color: 'grey.800', mb: 1 }}>
                  {t('audioCutter.controls.upload')}
                </Typography>

                <Typography variant="h6" sx={{ color: 'grey.600' }}>
                  {t('common.or')}
                </Typography>

                <Typography variant="body1" sx={{ color: 'grey.500', mt: 1 }}>
                  {t('audioCutter.messages.invalidFile')}
                </Typography>
              </Box>
            </Paper>
          </Box>
        </Box>
      ) : (
        <Stack spacing={3}>
          <audio
            ref={audioRef}
            src={audioUrl!}
            onTimeUpdate={handleTimeUpdate}
            onLoadedMetadata={() => {
              if (audioRef.current) {
                setDuration(audioRef.current.duration);
              }
            }}
            onEnded={() => setIsPlaying(false)}
            hidden
          >
            <track kind="captions" />
          </audio>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="body2" color="text.secondary" align="left">
              {audioFile.name}
            </Typography>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<FileUploadIcon />}
                onClick={handleNewFile}
                size="small"
              >
                {t('audioCutter.controls.upload')}
              </Button>
              {splitParts.length === 0 && (
                <ToggleButtonGroup
                  value={mode}
                  exclusive
                  onChange={handleModeChange}
                  aria-label={t('audioCutter.labels.mode')}
                  size="small"
                >
                  <ToggleButton
                    value="trim"
                    aria-label={t('audioCutter.controls.trim')}
                  >
                    <CropIcon sx={{ mr: 1 }} /> {t('audioCutter.controls.trim')}
                  </ToggleButton>
                  <ToggleButton
                    value="split"
                    aria-label={t('audioCutter.controls.cut')}
                  >
                    <ContentCutIcon sx={{ mr: 1 }} />{' '}
                    {t('audioCutter.controls.cut')}
                  </ToggleButton>
                </ToggleButtonGroup>
              )}
            </Box>

            {splitParts.length === 0 && (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <IconButton
                    onClick={handlePlayPause}
                    color="primary"
                    size="small"
                  >
                    {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
                  </IconButton>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ minWidth: 46, fontSize: '0.875rem' }}
                  >
                    {formatTime(currentTime)}
                  </Typography>
                  {mode === 'split' ? (
                    <Slider
                      value={cutPoint}
                      min={0}
                      max={duration}
                      onChange={handleSliderChange}
                      aria-label="Cut point"
                      valueLabelDisplay="auto"
                      valueLabelFormat={(value) => formatTime(value)}
                      sx={{
                        flexGrow: 1,
                        color: theme.palette.grey[300],
                        '& .MuiSlider-thumb': {
                          width: 16,
                          height: 16,
                          color: theme.palette.grey[400],
                          '&:hover, &.Mui-focusVisible': {
                            color: theme.palette.primary.main,
                            boxShadow: `0px 0px 0px 8px ${theme.palette.primary.main}20`,
                          },
                          '&.Mui-active': {
                            color: theme.palette.primary.main,
                            width: 20,
                            height: 20,
                            boxShadow: `0px 0px 0px 12px ${theme.palette.primary.main}20`,
                          },
                        },
                        '& .MuiSlider-track': {
                          height: 4,
                          borderRadius: 2,
                          color: theme.palette.grey[300],
                        },
                        '& .MuiSlider-rail': {
                          height: 4,
                          borderRadius: 2,
                          color: theme.palette.grey[300],
                        },
                        '& .MuiSlider-valueLabel': {
                          background: theme.palette.primary.main,
                          borderRadius: 1,
                          padding: '4px 8px',
                          '&:before': {
                            display: 'none',
                          },
                        },
                      }}
                    />
                  ) : (
                    <Slider
                      value={trimRange}
                      min={0}
                      max={100}
                      onChange={handleTrimRangeChange}
                      valueLabelDisplay="auto"
                      valueLabelFormat={(value) =>
                        formatTime((value / 100) * duration)
                      }
                      aria-label="Trim range"
                      sx={{
                        flexGrow: 1,
                        color: theme.palette.grey[300],
                        '& .MuiSlider-thumb': {
                          width: 16,
                          height: 16,
                          color: theme.palette.grey[400],
                          '&:hover, &.Mui-focusVisible': {
                            color: theme.palette.primary.main,
                            boxShadow: `0px 0px 0px 8px ${theme.palette.primary.main}20`,
                          },
                          '&.Mui-active': {
                            color: theme.palette.primary.main,
                            width: 20,
                            height: 20,
                            boxShadow: `0px 0px 0px 12px ${theme.palette.primary.main}20`,
                          },
                        },
                        '& .MuiSlider-track': {
                          height: 4,
                          borderRadius: 2,
                          color: theme.palette.grey[300],
                        },
                        '& .MuiSlider-rail': {
                          height: 4,
                          borderRadius: 2,
                          color: theme.palette.grey[300],
                        },
                        '& .MuiSlider-valueLabel': {
                          background: theme.palette.primary.main,
                          borderRadius: 1,
                          padding: '4px 8px',
                          '&:before': {
                            display: 'none',
                          },
                        },
                      }}
                    />
                  )}
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ minWidth: 46, fontSize: '0.875rem' }}
                  >
                    {formatTime(duration)}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    mt: -1,
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    {mode === 'split'
                      ? t('audioCutter.labels.cutPoints')
                      : t('audioCutter.labels.trimRange')}
                  </Typography>
                </Box>
              </>
            )}
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
            {splitParts.length > 0 && (
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<RestartAltIcon />}
                onClick={handleTryAgain}
                disabled={isProcessing}
                size="medium"
              >
                {t('audioCutter.controls.reset')}
              </Button>
            )}
            {splitParts.length === 0 && (
              <Button
                variant="contained"
                startIcon={mode === 'split' ? <ContentCutIcon /> : <CropIcon />}
                onClick={handleCut}
                disabled={isProcessing}
                size="medium"
                color="primary"
              >
                {isProcessing ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={16} color="inherit" />
                    {t('audioCutter.messages.processing')}
                  </Box>
                ) : (
                  getButtonText()
                )}
              </Button>
            )}
          </Box>

          {splitParts.length > 0 && (
            <List sx={{ width: '100%', bgcolor: 'background.paper', mt: 2 }}>
              {splitParts.map((part, index) => (
                <ListItem
                  key={part.url}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    bgcolor: 'background.paper',
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 1,
                    mb: 1,
                    py: 1.5,
                    px: 2,
                  }}
                >
                  <audio
                    key={part.url}
                    ref={(el) => {
                      previewRefs.current[part.url] = el;
                    }}
                    src={part.url}
                    onEnded={() =>
                      setPreviewStates((prev) => ({
                        ...prev,
                        [part.url]: false,
                      }))
                    }
                    hidden
                  >
                    <track kind="captions" />
                  </audio>
                  <IconButton
                    onClick={() => handlePreviewPlayPause(part.url)}
                    color="primary"
                    size="small"
                  >
                    {previewStates[part.url] ? (
                      <PauseIcon />
                    ) : (
                      <PlayArrowIcon />
                    )}
                  </IconButton>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {mode === 'split'
                        ? t('audioCutter.labels.parts')
                        : t('audioCutter.labels.trimRange')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formatTime(part.duration)}
                    </Typography>
                    {part.uploading && (
                      <LinearProgress
                        variant="determinate"
                        value={part.progress}
                        sx={{
                          mt: 1,
                          height: 2,
                          borderRadius: 1,
                        }}
                      />
                    )}
                  </Box>
                  <Button
                    variant="outlined"
                    color="secondary"
                    size="small"
                    startIcon={part.uploading ? null : <SaveIcon />}
                    onClick={() => handleSave(part, index)}
                    disabled={part.uploading}
                  >
                    {part.uploading ? (
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <CircularProgress size={16} color="inherit" />
                        {`${Math.round(part.progress)}%`}
                      </Box>
                    ) : (
                      t('audioCutter.controls.save')
                    )}
                  </Button>
                </ListItem>
              ))}
            </List>
          )}
        </Stack>
      )}

      {error && (
        <Typography color="error" sx={{ mt: 2 }}>
          {error}
        </Typography>
      )}
    </Box>
  );
};

export default AudioCutter;
